import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { PrimaryButton } from './PrimaryButton';
import { FloatingLabelInput } from './FloatingLabelInput';
import { CmsModal } from './CmsModal';
import {
  checkEmail,
  customerApiErrorMessage,
} from '../../lib/customer-api';
import { toast } from '../../lib/toast';

export function SignUpForm() {
  const navigate = useNavigate();
  const routerLoc = useLocation();
  // Carry the booking-flow resume target ("/your-appointment",
  // "/continue-booking", etc.) end-to-end. The customer enters the
  // signup flow from a booking page via "Create one" on /signin/customer,
  // which forwards `state.from`. We thread it through step 2 → and any
  // verification round-trip — so once they finish signing up they land
  // back on the basket they were building, not on /. Only honour
  // internal paths (and rule out /professional/* so this never bounces
  // a customer signup into the pro app).
  const fromState = (routerLoc.state as { from?: unknown } | null)?.from;
  const safeFrom =
    typeof fromState === 'string' &&
    fromState.startsWith('/') &&
    !fromState.startsWith('//') &&
    !fromState.startsWith('/professional')
      ? fromState
      : null;
  const [email, setEmail] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [termsError, setTermsError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Opens the CMS-backed Terms & Conditions overlay. Same pattern as
  // the pro wizard so admin-edited T&C copy shows up everywhere.
  const [termsOpen, setTermsOpen] = useState(false);

  const validateEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const handleSignUp = async () => {
    let hasError = false;
    if (!email || !validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      hasError = true;
    }
    if (!agreeToTerms) {
      setTermsError('You must accept the terms to continue.');
      hasError = true;
    }
    if (hasError) return;
    setEmailError(null);
    setTermsError(null);
    setSubmitting(true);
    try {
      // Step 1 just confirms the email is free. Real account creation
      // happens in step 2 once we have the name, password and address.
      await checkEmail(email.trim().toLowerCase());
      navigate('/signup/step2', {
        state: { email: email.trim().toLowerCase(), from: safeFrom },
      });
    } catch (err) {
      const msg = customerApiErrorMessage(err, 'Could not check email.');
      setEmailError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-[420px] mx-auto flex flex-col items-center gap-6 px-4">
      <div className="text-center w-full">
        <h1 className="font-['Playfair_Display',sans-serif] font-extrabold leading-[48px] text-[#8e2a2f] text-[34px] tracking-[-0.0944px] mb-1">
          Sign up to get started
        </h1>
        <p className="font-['DM_Sans',sans-serif] font-normal leading-[21px] text-[#908e8e] text-[14px] tracking-[-0.1px]" style={{ fontVariationSettings: "'opsz' 14" }}>
          Enter your details to proceed further
        </p>
      </div>

      <div className="w-full flex flex-col gap-6">
        <FloatingLabelInput
          label="Email"
          type="email"
          value={email}
          onChange={(value) => {
            setEmail(value);
            setEmailError(null);
          }}
          icon={``}
          className="w-full"
          error={!!emailError}
          errorMessage={emailError ?? ''}
        />

        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-[10px]">
            <button
              type="button"
              onClick={() => {
                setAgreeToTerms(!agreeToTerms);
                setTermsError(null);
              }}
              className={`size-[20px] rounded-[10px] flex items-center justify-center transition-colors cursor-pointer ${termsError && !agreeToTerms ? 'ring-1 ring-red-500' : ''}`}
              style={{
                backgroundImage: agreeToTerms
                  ? "linear-gradient(90deg, #8e2a2f 0%, #8e2a2f 100%)"
                  : "linear-gradient(90deg, rgba(58, 51, 53, 0.04) 0%, rgba(58, 51, 53, 0.04) 100%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)"
              }}
            >
              {agreeToTerms && (
                <span className="text-white text-[12px]">✓</span>
              )}
            </button>
            <p className="font-['DM_Sans',sans-serif] font-bold leading-[21px] text-[#908e8e] text-[14px] tracking-[-0.1px]" style={{ fontVariationSettings: "'opsz' 14" }}>
              I agree with{' '}
              <button
                type="button"
                onClick={() => setTermsOpen(true)}
                className="text-[#8e2a2f] font-bold underline underline-offset-2 hover:text-[#7a2228] transition-colors"
              >
                terms &amp; conditions
              </button>
            </p>
          </div>
          {termsError && (
            <p className="font-['DM_Sans',sans-serif] text-[12px] text-[#8e2a2f] pl-[30px] font-bold">
              {termsError}
            </p>
          )}
        </div>

        <PrimaryButton
          onClick={handleSignUp}
          className="w-full"
          disabled={submitting}
        >
          {submitting ? 'Checking…' : 'Sign Up'}
        </PrimaryButton>
      </div>


      {/* Returning-user link — mirrors the "Create one" link on the
          sign-in page so the two flows stay one click apart. */}
      <div className="text-center">
        <p className="font-['DM_Sans',sans-serif] text-[14px] text-[#908e8e]">
          Already have an account?{' '}
          <Link
            to="/signin/customer"
            state={safeFrom ? { from: safeFrom } : undefined}
            className="text-[#8e2a2f] font-bold hover:underline"
          >
            Sign In
          </Link>
        </p>
      </div>

      <CmsModal
        isOpen={termsOpen}
        onClose={() => setTermsOpen(false)}
        code="TERMS_CONDITIONS"
        userType="CUSTOMER"
        fallbackTitle="Terms & Conditions"
      />
    </div>
  );
}
